/* Mera Naksha — Auth Context.
 *
 * Two parallel session states because admin and customer cookies are kept
 * separate (admins can be testing the customer flow in the same browser):
 *
 *   - adminStatus / adminUser   ↔ cookie `mn_admin_token`
 *   - customerStatus / customer ↔ cookie `mn_customer_token`
 *
 * Each starts as `"checking"`, transitions to `"authenticated"` if /me
 * succeeds, otherwise `"unauthenticated"`. Pages use the role they care
 * about; ProtectedRoute consults both.
 *
 * Also installs a global axios `withCredentials: true` default so every
 * downstream API call carries cookies automatically.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// ─── Bearer-token fallback ──────────────────────────────────────────
// HttpOnly cookies are the primary auth path. But when the production
// frontend and backend live on different origins, the browser can drop
// the cookie despite `withCredentials: true` (SameSite/3rd-party-cookie
// blocking). To keep admins productive in that case we ALSO accept an
// `Authorization: Bearer <token>` header — server-side `_extract_token`
// reads whichever path arrives. The token is returned by /auth/login
// alongside the cookie, persisted in sessionStorage, and attached as a
// header by the interceptor below.
const ADMIN_TOKEN_KEY = "mn_admin_access_token";

const readAdminToken = () => {
  try {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY) || "";
  } catch {
    return "";
  }
};

const writeAdminToken = (tok) => {
  try {
    if (tok) sessionStorage.setItem(ADMIN_TOKEN_KEY, tok);
    else sessionStorage.removeItem(ADMIN_TOKEN_KEY);
  } catch {
    /* sessionStorage blocked — fall back to cookie-only auth */
  }
};

// Attach the Bearer token to every /api/admin/* request. Cookie still
// flows automatically via withCredentials — the header is additive.
axios.interceptors.request.use((config) => {
  const url = config.url || "";
  if (url.includes("/api/admin/") || url.endsWith("/api/admin")) {
    const tok = readAdminToken();
    if (tok) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization && !config.headers.authorization) {
        config.headers.Authorization = `Bearer ${tok}`;
      }
    }
  }
  return config;
});

const AuthContext = createContext({
  adminStatus: "checking",
  adminUser: null,
  customerStatus: "checking",
  customer: null,
  loginAdmin: async () => {},
  logoutAdmin: async () => {},
  requestOtp: async () => {},
  verifyOtp: async () => {},
  logoutCustomer: async () => {},
  refreshAll: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [adminStatus, setAdminStatus] = useState("checking");
  const [adminUser, setAdminUser] = useState(null);
  const [customerStatus, setCustomerStatus] = useState("checking");
  const [customer, setCustomer] = useState(null);

  const refreshAdmin = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/auth/me`);
      setAdminUser(data);
      setAdminStatus("authenticated");
    } catch {
      // Stale/expired Bearer would loop us into 401 forever — wipe it.
      writeAdminToken("");
      setAdminUser(null);
      setAdminStatus("unauthenticated");
    }
  }, []);

  const refreshCustomer = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/customer/auth/me`);
      setCustomer(data);
      setCustomerStatus("authenticated");
    } catch {
      setCustomer(null);
      setCustomerStatus("unauthenticated");
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshAdmin(), refreshCustomer()]);
  }, [refreshAdmin, refreshCustomer]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const loginAdmin = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/admin/auth/login`, { email, password });
    // Cache the bearer token as a same-origin-cookie fallback.
    if (data?.access_token) writeAdminToken(data.access_token);
    setAdminUser(data);
    setAdminStatus("authenticated");
    return data;
  }, []);

  const logoutAdmin = useCallback(async () => {
    try {
      await axios.post(`${API}/admin/auth/logout`);
    } finally {
      writeAdminToken("");
      setAdminUser(null);
      setAdminStatus("unauthenticated");
    }
  }, []);

  const requestOtp = useCallback(async (phone) => {
    const { data } = await axios.post(`${API}/customer/auth/request-otp`, { phone });
    // Fire GA4 event AFTER the API call so we only count real send
    // attempts (client validation failures don't inflate the funnel).
    // No PII — `phone` is intentionally NOT included.
    trackEvent("otp_requested", { method: "sms" });
    return data; // { ok, expires_in, dev_mode, dev_otp? }
  }, []);

  const verifyOtp = useCallback(async (phone, code, name) => {
    const { data } = await axios.post(`${API}/customer/auth/verify-otp`, { phone, code, name });
    setCustomer(data);
    setCustomerStatus("authenticated");
    trackEvent("otp_verified", { method: "sms" });
    return data;
  }, []);

  const logoutCustomer = useCallback(async () => {
    try {
      await axios.post(`${API}/customer/auth/logout`);
    } finally {
      setCustomer(null);
      setCustomerStatus("unauthenticated");
    }
  }, []);

  // Memoised context value — without this, every parent re-render produces
  // a fresh object reference and every consumer of `useAuth()` re-renders
  // even when none of the auth state actually changed.
  const ctxValue = useMemo(
    () => ({
      // Admin
      adminStatus,
      adminUser,
      loginAdmin,
      logoutAdmin,
      // Customer
      customerStatus,
      customer,
      requestOtp,
      verifyOtp,
      logoutCustomer,
      // Bulk
      refreshAll,
    }),
    [
      adminStatus,
      adminUser,
      loginAdmin,
      logoutAdmin,
      customerStatus,
      customer,
      requestOtp,
      verifyOtp,
      logoutCustomer,
      refreshAll,
    ],
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
